/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    let blocks: Array<CodeBlock> = [];
    const MODBUSDEVICES = feature.getSettingValue('MODBUSDEVICES');
    if (!feature.getEnabled()) {
        return blocks;
    }
    const MODBUS_DEVICE_COUNT = MODBUSDEVICES.length;
    const MASTER_SERIAL_PORT = feature.getSettingValue('MASTER_SERIAL_PORT');
    let callback_modbus_slave_reply = '';
    let setupCode = '';

    for (let i = 0; i < MODBUSDEVICES.length; i += 1) {
        const codeBlocks = MODBUSDEVICES[i].getCodeBlocks();
        for (let j = 0; j < codeBlocks.length; j += 1) {
            const block = codeBlocks[j];
            if (block.location === 'callback_modbus_slave_reply') {
                callback_modbus_slave_reply += block.template;
            } else if (block.location === 'MODBUS_POLL_SAMPLE') {
                setupCode += block.template;
            } else if (block.location === 'MODBUS_DEVICE_SETUP') {
                if (MODBUSDEVICES[i].getSettingValue('PROTOCOL') === 'modbus_TCP') {
                    setupCode += block.template;
                }
            } else {
                blocks = blocks.concat(block);
            }
        }
    }

    blocks.push({
        location: 'callback_modbus_slave_reply',
        blockType: BlockType.CODE,
        template: callback_modbus_slave_reply,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
#define MODBUS_TIMEOUT_OFFLINE_COUNT 6
declare MODBUS_MASTER_INTERFACE  as byte            
declare modbus_master_timeouts(MODBUS_MAX_REQUESTS,2) as byte 'first is device id, second is timeout count
declare sub modbus_master_enqueue_request(devaddr as byte, request as byte, start as word, count as word, regdata as string, interface_number as byte, protocol as modbusProtocol)`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub modbus_master_enqueue_request(devaddr as byte, request as byte, start as word, count as word, regdata as string, interface_number as byte, protocol as modbusProtocol)
    dim count_max as byte=255
    dim modbus_i as byte=0
    for modbus_i=0 to MODBUS_MAX_REQUESTS-1
        if modbus_master_timeouts(modbus_i,0)=devaddr AND modbus_master_timeouts(modbus_i,1) >= MODBUS_TIMEOUT_OFFLINE_COUNT then
            exit sub
        end if
    next modbus_i
    modbus_master_request(devaddr,request,start,count,regdata,interface_number,protocol,0)
end sub`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        template: `
dim MODBUS_MASTER_INTERFACE  as byte
dim modbus_master_timeouts(MODBUS_MAX_REQUESTS,2) as byte`,
    });
    if (feature.getSettingValue('MASTERPROTOCOL') === 'modbus_RTU') {
        blocks.push({
            location: 'on_ser_data_arrival',
            blockType: BlockType.CODE,
            id: `MODBUS_RECV_${MASTER_SERIAL_PORT}`,
            template: `
if ser.num=MODBUS_MASTER_INTERFACE then
    'modbus master data
    modbus_master_receive(ser.getdata(255),modbus_RTU)
end if`,
        });
        setupCode += `\n    MODBUS_MASTER_INTERFACE=${MASTER_SERIAL_PORT}`;
    } else {
        setupCode += '\n    MODBUS_MASTER_INTERFACE=sock_get("MBM")';
    }

    blocks.push({
        location: 'modbus_setup',
        blockType: BlockType.CODE,
        template: setupCode,
    });

    blocks.push({
        location: 'on_ser_data_sent',
        blockType: BlockType.CODE,
        id: 'modbus_proc_ser_data_sent',
        template: `
    modbus_proc_ser_data_sent()`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
dim modbus_i as byte=0
for modbus_i=0 to MODBUS_MAX_REQUESTS-1
    modbus_master_timeouts(modbus_i,0)=255
    modbus_master_timeouts(modbus_i,1)=0
next modbus_i
`,
    });

    blocks.push({
        location: 'callback_modbus_slave_reply_timeout',
        blockType: BlockType.CODE,
        template: `
dim count_max as byte=0
dim modbus_i as byte=0
for modbus_i=0 to MODBUS_MAX_REQUESTS-1
    if modbus_master_timeouts(modbus_i,0)=255 OR modbus_master_timeouts(modbus_i,0)=request_item.device_id then
        count_max=modbus_i
        exit for
    end if
next modbus_i
modbus_master_timeouts(count_max,0)=request_item.device_id
modbus_master_timeouts(count_max,1)=modbus_master_timeouts(count_max,1)+1
if modbus_master_timeouts(count_max,1) >= MODBUS_TIMEOUT_OFFLINE_COUNT then
    sys.debugprint("modbus> device "+str(request_item.device_id)+" is offline")
end if
`,
    });

    blocks.push({
        location: 'callback_modbus_slave_reply',
        blockType: BlockType.CODE,
        template: `
dim count_max as byte=255
dim modbus_i as byte=0
for modbus_i=0 to MODBUS_MAX_REQUESTS-1
    if modbus_master_timeouts(modbus_i,0)=request_item.device_id then
        modbus_master_timeouts(modbus_i,0)=255
        modbus_master_timeouts(modbus_i,1)=0
        exit for
    end if
next modbus_i
`,
    });

    return blocks;
};
